import { MutableRefObject } from 'react';
import { Editor, Extensions, JSONContent } from '@tiptap/core';
import { ReactRenderer } from '@tiptap/react';
import Paragraph from '@tiptap/extension-paragraph';
import TextStyle from '@tiptap/extension-text-style';
import Text from '@tiptap/extension-text';
import Document from '@tiptap/extension-document';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import ListItem from '@tiptap/extension-list-item';
import History from '@tiptap/extension-history';
import Heading from '@tiptap/extension-heading';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import CharacterCount from '@tiptap/extension-character-count';

import {
  CustomFontFamily,
  HashtagLinks,
  ReadonlyPlaceholder,
  READONLY_PLACEHOLDER_NODE,
  SmallParagraph,
} from './richtext-custom-extensions';
import {
  FONT_CALIBRE_SCHOOL,
  FONT_NSW_HANDWRITING,
} from 'approot/shared/styles/typography.style';
import {
  BlockStyle,
  BLOCK_STYLE_PARAGRAPH,
  FontFamily,
  TextAlign as TextAlignType,
  TEXT_ALIGN_LEFT,
  TEXT_ALIGN_RIGHT,
  TEXT_ALIGN_CENTRE,
  BLOCK_STYLE_PARAGRAPH_SMALL,
} from './richtext-toolbar.constants';
import RichtextHashtagDropdown from './richtext-hashtag-dropdown';
import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';
import { RichtextPreventNewLine } from './richtext-prevent-new-line';

export function toggleBold(editor: Editor) {
  editor.chain().focus().toggleBold().run();
}

export function isSelectionBold(editor: Editor) {
  return editor.isActive('bold');
}

export function toggleItalic(editor: Editor) {
  editor.chain().focus().toggleItalic().run();
}

export function isSelectionItalic(editor: Editor) {
  return editor.isActive('italic');
}

export function toggleUnderline(editor: Editor) {
  editor.chain().focus().toggleUnderline().run();
}

export function isSelectionUnderlined(editor: Editor) {
  return editor.isActive('underline');
}

export function toggleBulletList(editor: Editor) {
  editor.chain().focus().toggleBulletList().run();
}

export function isSelectionBulletList(editor: Editor) {
  return editor.isActive('bulletList');
}

export function toggleOrderedList(editor: Editor) {
  editor.chain().focus().toggleOrderedList().run();
}

export function setLink(editor: Editor, href: string) {
  editor.commands.setLink({ href, target: '_blank' });
}

export function removeLink(editor: Editor) {
  editor.commands.unsetLink();
}

export function getLink(editor: Editor): string | undefined {
  return editor.getAttributes('link').href;
}

export function isSelectionOrderedList(editor: Editor) {
  return editor.isActive('orderedList');
}

export function undo(editor: Editor) {
  editor.chain().focus().undo().run();
}

export function redo(editor: Editor) {
  editor.chain().focus().redo().run();
}

export function setBlockStyle(
  editor: Editor,
  style: BlockStyle,
  fontFamily: FontFamily
) {
  switch (style) {
    case BLOCK_STYLE_PARAGRAPH:
      editor.chain().focus().setParagraph().setFontFamily(fontFamily).run();
      break;
    case BLOCK_STYLE_PARAGRAPH_SMALL:
      editor.chain().focus().setSmall().setFontFamily(fontFamily).run();
      break;
  }
}

export function getBlockStyle(editor: Editor): BlockStyle {
  if (editor.isActive(BLOCK_STYLE_PARAGRAPH_SMALL))
    return BLOCK_STYLE_PARAGRAPH_SMALL;

  return BLOCK_STYLE_PARAGRAPH;
}

export function setFontFamily(editor: Editor, fontFamily: FontFamily) {
  editor.chain().focus().setFontFamily(fontFamily).run();
}

export function getFontFamily(editor: Editor) {
  return editor.getAttributes('textStyle').fontFamily;
}

export function getFontFamilyLabel(fontFamily: FontFamily) {
  switch (fontFamily) {
    case FONT_CALIBRE_SCHOOL:
      return 'Calibre-R School';
    case FONT_NSW_HANDWRITING:
      return 'NSW Handwriting';
    default:
      return '';
  }
}

export function getBlockStyleLabel(blockStyle: BlockStyle) {
  switch (blockStyle) {
    case BLOCK_STYLE_PARAGRAPH_SMALL:
      return 'Small';
    default:
      return 'Medium';
  }
}

export function setBlockHighlight(editor: Editor, colour: string) {
  editor.chain().focus().toggleHighlight({ color: colour }).run();
}

export function isRichtextEmpty(richtext?: JSONContent) {
  return !richtext || Object.keys(richtext).length === 0;
}

export function hasTextInRichtext(obj: JSONContent) {
  if (obj && typeof obj === 'object') {
    if (obj.type === 'text' && obj.text && obj.text.trim() !== '') {
      return true;
    }

    if (Array.isArray(obj.content)) {
      for (const item of obj.content) {
        if (hasTextInRichtext(item)) {
          return true;
        }
      }
    }
  }
  return false;
}

export function isTextAlign(editor: Editor, textAlign: TextAlignType): boolean {
  return editor.isActive({ textAlign });
}

export function setTextAlign(editor: Editor, textAlign: TextAlignType) {
  editor.chain().focus().setTextAlign(textAlign).run();
}

export type Settings = {
  placeholder?: string;
  readonlyPlaceholder?: string;
  suggestions?: { name: string; link: string }[];
  suggestionsDropdownElement?: MutableRefObject<HTMLDivElement | null>;
  canChangeBlockType?: boolean;
  canBold?: boolean;
  canItalic?: boolean;
  canUnderline?: boolean;
  canAddLists?: boolean;
  canChangeHistory?: boolean;
  canChangeFonts?: boolean;
  canAddLinks?: boolean;
  canFollowLinks?: boolean;
  canAlignText?: boolean;
  canHighlight?: boolean;
  maxCharacterCount?: number;
  preventNewLine?: boolean;
  handlePaste?: (view: unknown, event: ClipboardEvent) => boolean;
};

export function getExtensions(settings: Settings) {
  const extensions: Extensions = [
    Document,
    Text,
    TextStyle,
    Paragraph,
    SmallParagraph,
  ];

  if (settings.preventNewLine) {
    extensions.push(RichtextPreventNewLine);
  }

  if (settings.maxCharacterCount) {
    extensions.push(
      CharacterCount.configure({ limit: settings.maxCharacterCount })
    );
  }

  if (settings.placeholder) {
    extensions.push(
      Placeholder.configure({
        placeholder: settings.placeholder,
      })
    );
  }

  if (settings.readonlyPlaceholder) {
    extensions.push(ReadonlyPlaceholder);
  }

  if (settings.suggestions) {
    const items = settings.suggestions;
    const dropdownElement = settings.suggestionsDropdownElement;

    extensions.push(
      HashtagLinks.configure({
        suggestion: {
          items: () => {
            return items;
          },
          render: () => {
            let component: any;

            return {
              onStart: props => {
                if (dropdownElement?.current) {
                  component = new ReactRenderer(RichtextHashtagDropdown, {
                    props,
                    editor: props.editor,
                  });
                  dropdownElement.current.innerHTML = '';
                  dropdownElement.current.appendChild(component.element);
                }
              },
              onUpdate: props => {
                if (dropdownElement?.current) {
                  component = new ReactRenderer(RichtextHashtagDropdown, {
                    props,
                    editor: props.editor,
                  });
                  dropdownElement.current.innerHTML = '';
                  dropdownElement.current.appendChild(component.element);
                }
              },
              onKeyDown: props => {
                if (dropdownElement?.current) {
                  if (props.event.key === ESCAPE) {
                    dropdownElement.current.innerHTML = '';

                    return true;
                  }

                  return component?.ref?.onKeyDown(props);
                }
              },
              onExit: () => {
                if (dropdownElement?.current) {
                  dropdownElement.current.innerHTML = '';
                }
              },
            };
          },
        },
      })
    );
  }

  if (settings.canChangeBlockType) {
    extensions.push(Heading.configure({ levels: [1, 2] }));
  }

  if (settings.canBold) {
    extensions.push(Bold);
  }

  if (settings.canItalic) {
    extensions.push(Italic);
  }

  if (settings.canAddLists) {
    extensions.push(...[ListItem, BulletList, OrderedList]);
  }

  if (settings.canChangeHistory) {
    extensions.push(History);
  }

  if (settings.canUnderline) {
    extensions.push(Underline);
  }

  if (settings.canChangeFonts) {
    extensions.push(CustomFontFamily);
  }

  if (settings.canAddLinks) {
    extensions.push(Link.configure({ openOnClick: settings.canFollowLinks }));
  }

  if (settings.canAlignText) {
    // Only nodes this call actually registered. `setTextAlign` runs
    // `types.every(type => updateAttributes(type, …))` and `updateAttributes`
    // returns false for a type the schema does not know, so naming `heading`
    // without `canChangeBlockType` makes the command report failure and
    // short-circuits before `small` is ever aligned.
    extensions.push(
      TextAlign.configure({
        alignments: [TEXT_ALIGN_LEFT, TEXT_ALIGN_RIGHT, TEXT_ALIGN_CENTRE],
        types: [
          'paragraph',
          'small',
          ...(settings.canChangeBlockType ? ['heading'] : []),
        ],
      })
    );
  }

  if (settings.canHighlight) {
    extensions.push(
      Highlight.configure({
        multicolor: true,
      })
    );
  }

  return extensions;
}

export function includesReadonlyPlaceholder(value?: JSONContent) {
  const nodes = value?.content;
  // assumes readonly placeholder is only ever the first node
  return !!nodes
    ?.at(0)
    ?.content?.find(c => c.type === READONLY_PLACEHOLDER_NODE);
}

export function removeInlineFontStyle(text: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');

  doc.querySelectorAll('*').forEach(element => {
    (element as HTMLElement).style.removeProperty('font-family');
  });

  return doc;
}

export function removeAnchorElements(document: Document) {
  const links = document.getElementsByTagName('a');

  for (let i = links.length - 1; i >= 0; i--) {
    const link = links[i];
    if (link.textContent) {
      const textNode = document.createTextNode(link.textContent);
      link.parentNode?.replaceChild(textNode, link);
    }
  }
  return document;
}
